import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import ReactFlow, {
    ReactFlowProvider,
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    useReactFlow,
    ConnectionMode,
    Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import NearMeIcon from '@mui/icons-material/NearMe';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MarkovNode from './MarkovNode';
import MarkovGlowEdge from './MarkovGlowEdge';
import MarkovSelfEdge from './MarkovSelfEdge';
import './MarkovGraphCanvas.css';

const THEME = {
    bg: '#0a0a0a',
    cardBg: '#18181b',
    border: '#333',
    primary: '#2cbb5d',
    secondary: '#a1a1aa',
};

const NODE_CENTER = { x: 54, y: 24 };

const nodeTypes = { markov: MarkovNode };
const edgeTypes = { markov: MarkovGlowEdge, self: MarkovSelfEdge };

/** Default demo chain: 5 states, 8 directed transitions. */
const DEFAULT_NODES = [
    { id: '1', type: 'markov', position: { x: 280, y: 40 }, data: { label: 'A' } },
    { id: '2', type: 'markov', position: { x: 480, y: 160 }, data: { label: 'B' } },
    { id: '3', type: 'markov', position: { x: 400, y: 340 }, data: { label: 'C' } },
    { id: '4', type: 'markov', position: { x: 160, y: 340 }, data: { label: 'D' } },
    { id: '5', type: 'markov', position: { x: 80, y: 160 }, data: { label: 'E' } },
];

const DEFAULT_EDGES = [
    { id: 'e1-2', source: '1', target: '2', type: 'markov', label: '0.4', data: { weight: '0.4' } },
    { id: 'e2-3', source: '2', target: '3', type: 'markov', label: '0.5', data: { weight: '0.5' } },
    { id: 'e3-4', source: '3', target: '4', type: 'markov', label: '0.6', data: { weight: '0.6' } },
    { id: 'e4-5', source: '4', target: '5', type: 'markov', label: '0.3', data: { weight: '0.3' } },
    { id: 'e5-1', source: '5', target: '1', type: 'markov', label: '0.5', data: { weight: '0.5' } },
    { id: 'e1-3', source: '1', target: '3', type: 'markov', label: '0.2', data: { weight: '0.2' } },
    { id: 'e2-4', source: '2', target: '4', type: 'markov', label: '0.3', data: { weight: '0.3' } },
    { id: 'e5-2', source: '5', target: '2', type: 'markov', label: '0.4', data: { weight: '0.4' } },
];

function nextNodeId(nodes) {
    const max = nodes.reduce((m, n) => Math.max(m, parseInt(n.id, 10) || 0), 0);
    return String(max + 1);
}

function resolvePathEdgeIds(pathNodes, policyIds, edges) {
    if (!edges?.length) return [...(policyIds || [])];
    const used = new Set();
    const ordered = [];

    const pick = (src, tgt, preferredId) => {
        if (preferredId) {
            const byId = edges.find((e) => e.id === preferredId && !used.has(e.id));
            if (byId) return byId;
        }
        return (
            edges.find((e) => e.source === src && e.target === tgt && !used.has(e.id)) ||
            edges.find((e) => e.source === src && e.target === tgt)
        );
    };

    if (pathNodes?.length >= 2) {
        for (let i = 0; i < pathNodes.length - 1; i++) {
            const edge = pick(pathNodes[i], pathNodes[i + 1], policyIds?.[i]);
            if (edge) {
                ordered.push(edge.id);
                used.add(edge.id);
            }
        }
        if (ordered.length) return ordered;
    }

    (policyIds || []).forEach((id) => {
        if (edges.some((e) => e.id === id)) ordered.push(id);
    });
    return ordered;
}

function buildSolutionMaps(problem, result, optimalEdgeIds, pathStep, edges) {
    const maps = {
        edgeState: {},
        edgeFlow: {},
        nodeRole: {},
        nodeStationary: {},
        nodeValue: {},
        pathEdgeOrder: [],
        startId: null,
        goalId: null,
    };
    if (!result) return maps;

    const policyIds = optimalEdgeIds || result.optimal_edge_ids || [];
    const pathNodes = result.path_nodes || [];

    if (problem === 'shortest_path' || problem === 'max_probability') {
        const pathEdges = resolvePathEdgeIds(pathNodes, policyIds, edges);
        maps.pathEdgeOrder = pathEdges;
        maps.startId = pathNodes[0] || null;
        maps.goalId = pathNodes[pathNodes.length - 1] || null;

        // Dim everything first when we have a path
        if (pathEdges.length) {
            (edges || []).forEach((e) => {
                maps.edgeState[e.id] = 'dim';
            });
        }

        pathEdges.forEach((eid, i) => {
            if (i < pathStep) maps.edgeState[eid] = 'done';
            else if (i === pathStep) maps.edgeState[eid] = 'active';
            else maps.edgeState[eid] = 'upcoming';
        });

        // Permanent start / goal colors
        if (maps.startId) maps.nodeRole[maps.startId] = 'start';
        if (maps.goalId) maps.nodeRole[maps.goalId] = 'goal';

        // Cursor = node reached so far (after completing pathStep edges)
        const cursorIdx = Math.min(pathStep, Math.max(0, pathNodes.length - 1));
        const cursorId = pathNodes[cursorIdx];
        if (cursorId && cursorId !== maps.startId && cursorId !== maps.goalId) {
            maps.nodeRole[cursorId] = 'cursor';
        } else if (cursorId === maps.startId && pathStep === 0) {
            maps.nodeRole[cursorId] = 'start'; // keep start; pulse via class
        } else if (cursorId === maps.goalId && pathStep >= pathEdges.length) {
            maps.nodeRole[cursorId] = 'goal';
        }

        // Soft visited trail on intermediate nodes already passed
        for (let i = 1; i < cursorIdx; i++) {
            const nid = pathNodes[i];
            if (nid !== maps.startId && nid !== maps.goalId && !maps.nodeRole[nid]) {
                maps.nodeRole[nid] = 'visited';
            }
        }

        return maps;
    }

    if (problem === 'value_iteration') {
        policyIds.forEach((eid) => {
            maps.edgeState[eid] = 'policy';
        });
        // Also mark by matching if ids differ
        if (edges?.length && policyIds.length) {
            const idSet = new Set(policyIds);
            edges.forEach((e) => {
                if (idSet.has(e.id)) maps.edgeState[e.id] = 'policy';
            });
        }
        if (result.values) {
            Object.entries(result.values).forEach(([id, v]) => {
                maps.nodeValue[id] = v;
            });
        }
        return maps;
    }

    if (problem === 'analyze' && result.stationary) {
        const vals = Object.values(result.stationary);
        const max = Math.max(...vals, 1e-9);
        Object.entries(result.stationary).forEach(([id, p]) => {
            maps.nodeStationary[id] = Number(p) / max;
        });
        const nodeIds = result.node_ids || Object.keys(result.stationary);
        const matrix = result.transition_matrix;
        if (matrix && nodeIds.length && edges?.length) {
            edges.forEach((e) => {
                const i = nodeIds.indexOf(e.source);
                const j = nodeIds.indexOf(e.target);
                const p = i >= 0 && j >= 0 ? matrix[i]?.[j] : 0;
                if (p > 0.04) {
                    maps.edgeState[e.id] = 'flow';
                    maps.edgeFlow[e.id] = Math.min(1, p);
                }
            });
        }
    }

    return maps;
}

function MarkovGraphCanvasInner({
    onGraphChange,
    problem,
    result,
    optimalEdgeIds = [],
}) {
    const [nodes, setNodes, onNodesChange] = useNodesState(DEFAULT_NODES);
    const [edges, setEdges, onEdgesChange] = useEdgesState(DEFAULT_EDGES);
    const [tool, setTool] = useState('select');
    const [pathStep, setPathStep] = useState(0);
    const [weightDialog, setWeightDialog] = useState(null);
    const [weightInput, setWeightInput] = useState('0.5');
    const [labelDialog, setLabelDialog] = useState(null);
    const [labelInput, setLabelInput] = useState('');
    const [selectedEdgeIds, setSelectedEdgeIds] = useState(() => new Set());
    const { screenToFlowPosition } = useReactFlow();

    const edgeMode = tool === 'edge';

    useEffect(() => {
        if (typeof onGraphChange === 'function') {
            onGraphChange({ nodes, edges });
        }
    }, [nodes, edges, onGraphChange]);

    const solution = useMemo(
        () => buildSolutionMaps(problem, result, optimalEdgeIds, pathStep, edges),
        [problem, result, optimalEdgeIds, pathStep, edges]
    );

    const pathEdgeCount = solution.pathEdgeOrder.length;

    useEffect(() => {
        setPathStep(0);
    }, [result, problem]);

    useEffect(() => {
        if (!result) return undefined;
        if (problem !== 'shortest_path' && problem !== 'max_probability') return undefined;
        if (pathEdgeCount === 0) return undefined;
        const id = setInterval(() => {
            setPathStep((s) => (s >= pathEdgeCount ? 0 : s + 1));
        }, 1100);
        return () => clearInterval(id);
    }, [result, problem, pathEdgeCount]);

    const decoratedNodes = useMemo(
        () =>
            nodes.map((n) => ({
                ...n,
                type: 'markov',
                draggable: tool === 'select',
                connectable: edgeMode,
                data: {
                    ...n.data,
                    label: n.data?.label ?? n.id,
                    connectable: edgeMode,
                    solutionRole: solution.nodeRole[n.id] || 'none',
                    stationaryStrength: solution.nodeStationary[n.id] ?? 0,
                    stateValue: solution.nodeValue[n.id],
                    isPathStart: solution.startId === n.id,
                    isPathGoal: solution.goalId === n.id,
                },
            })),
        [nodes, solution, tool, edgeMode]
    );

    const openWeightEdit = useCallback((edge) => {
        if (!edge) return;
        setWeightDialog({
            source: edge.source,
            target: edge.target,
            editId: edge.id,
        });
        setWeightInput(String(edge.data?.weight ?? edge.label ?? '0.5'));
    }, []);

    const decoratedEdges = useMemo(
        () =>
            edges.map((e) => {
                let state = solution.edgeState[e.id] || 'off';
                const flowStrength = solution.edgeFlow[e.id] || 0;
                const selected = e.selected || selectedEdgeIds.has(e.id);
                return {
                    ...e,
                    selected,
                    type: e.source === e.target ? 'self' : 'markov',
                    animated: state === 'active' || state === 'flow',
                    zIndex: state === 'active' || state === 'done' ? 10 : 0,
                    data: {
                        ...e.data,
                        solutionState: state,
                        flowStrength,
                        onEditWeight: () => openWeightEdit(e),
                    },
                    label: e.label ?? e.data?.weight,
                    style: {
                        ...(e.style || {}),
                    },
                };
            }),
        [edges, solution, selectedEdgeIds, openWeightEdit]
    );

    const onConnect = useCallback((params) => {
        if (!params?.source || !params?.target) return;
        setWeightDialog({
            source: params.source,
            target: params.target,
            sourceHandle: params.sourceHandle,
            targetHandle: params.targetHandle,
        });
        setWeightInput('0.5');
    }, []);

    const onPaneClick = useCallback(
        (ev) => {
            if (tool !== 'addNode') return;
            const pos = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
            const id = nextNodeId(nodes);
            const label = `S${id}`;
            setNodes((nds) =>
                nds.concat({
                    id,
                    type: 'markov',
                    position: { x: pos.x - NODE_CENTER.x, y: pos.y - NODE_CENTER.y },
                    data: { label },
                })
            );
            setLabelDialog({ nodeId: id, isNew: true });
            setLabelInput(label);
        },
        [tool, screenToFlowPosition, nodes, setNodes]
    );

    const confirmWeight = () => {
        if (!weightDialog) return;
        const { source, target, sourceHandle, targetHandle, editId } = weightDialog;
        const w = weightInput.trim() || '0.5';

        if (editId) {
            setEdges((eds) =>
                eds.map((e) =>
                    e.id === editId
                        ? { ...e, label: w, data: { ...e.data, weight: w } }
                        : e
                )
            );
            setWeightDialog(null);
            return;
        }

        const id = `e${source}-${target}-${Date.now()}`;
        const isSelf = source === target;
        setEdges((eds) =>
            eds.concat({
                id,
                source,
                target,
                sourceHandle: sourceHandle || undefined,
                targetHandle: targetHandle || undefined,
                type: isSelf ? 'self' : 'markov',
                label: w,
                data: { weight: w },
            })
        );
        setWeightDialog(null);
    };

    const confirmLabel = () => {
        if (!labelDialog) return;
        const { nodeId } = labelDialog;
        const label = labelInput.trim() || nodeId;
        setNodes((nds) =>
            nds.map((n) =>
                n.id === nodeId ? { ...n, data: { ...n.data, label } } : n
            )
        );
        setLabelDialog(null);
    };

    const onNodeDoubleClick = useCallback((_ev, node) => {
        setLabelDialog({ nodeId: node.id });
        setLabelInput(node.data?.label || node.id);
    }, []);

    const onEdgeDoubleClick = useCallback(
        (_ev, edge) => {
            openWeightEdit(edge);
        },
        [openWeightEdit]
    );

    const onEdgeClick = useCallback((_ev, edge) => {
        setSelectedEdgeIds(new Set([edge.id]));
        setEdges((eds) =>
            eds.map((e) => ({
                ...e,
                selected: e.id === edge.id,
            }))
        );
        setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
    }, [setEdges, setNodes]);

    const onNodeClick = useCallback((_ev, node) => {
        setSelectedEdgeIds(new Set());
        setEdges((eds) => eds.map((e) => ({ ...e, selected: false })));
        setNodes((nds) =>
            nds.map((n) => ({
                ...n,
                selected: n.id === node.id,
            }))
        );
    }, [setEdges, setNodes]);

    const deleteSelected = useCallback(() => {
        setEdges((eds) => {
            const drop = new Set(
                eds.filter((e) => e.selected || selectedEdgeIds.has(e.id)).map((e) => e.id)
            );
            return eds.filter((e) => !drop.has(e.id));
        });
        setNodes((nds) => {
            const removed = new Set(nds.filter((n) => n.selected).map((n) => n.id));
            if (removed.size) {
                setEdges((eds) =>
                    eds.filter((e) => !removed.has(e.source) && !removed.has(e.target))
                );
            }
            return nds.filter((n) => !n.selected);
        });
        setSelectedEdgeIds(new Set());
    }, [setNodes, setEdges, selectedEdgeIds]);

    useEffect(() => {
        const onKey = (e) => {
            if (document.activeElement?.tagName === 'INPUT') return;
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                deleteSelected();
                return;
            }
            if (e.key === 'Enter') {
                const edge = edges.find((ed) => ed.selected || selectedEdgeIds.has(ed.id));
                if (edge) {
                    e.preventDefault();
                    openWeightEdit(edge);
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [deleteSelected, edges, selectedEdgeIds, openWeightEdit]);

    const toolBtn = (_id, active) =>
        active
            ? { bgcolor: `${THEME.primary} !important`, color: '#000 !important' }
            : {};

    return (
        <Box
            sx={{
                height: { xs: 480, md: 560 },
                borderRadius: 2,
                border: `1px solid ${THEME.border}`,
                overflow: 'hidden',
                bgcolor: THEME.bg,
                position: 'relative',
            }}
        >
            <ReactFlow
                nodes={decoratedNodes}
                edges={decoratedEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onPaneClick={(ev) => {
                    setSelectedEdgeIds(new Set());
                    onPaneClick(ev);
                }}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onNodeDoubleClick={onNodeDoubleClick}
                onEdgeDoubleClick={onEdgeDoubleClick}
                nodesConnectable={edgeMode}
                elementsSelectable
                edgesFocusable
                deleteKeyCode={null}
                connectionMode={ConnectionMode.Loose}
                connectionLineStyle={{
                    stroke: THEME.primary,
                    strokeWidth: 2.5,
                }}
                connectionLineType="bezier"
                panOnDrag={tool === 'select'}
                selectionOnDrag={false}
                fitView
                minZoom={0.4}
                maxZoom={1.8}
                defaultEdgeOptions={{ type: 'markov', selectable: true, focusable: true }}
                proOptions={{ hideAttribution: true }}
            >
                <Background color={THEME.border} gap={20} />
                <Controls />
                <Panel position="top-left">
                    <ButtonGroup
                        size="small"
                        sx={{
                            bgcolor: THEME.cardBg,
                            border: `1px solid ${THEME.border}`,
                            '& .MuiButton-root': {
                                color: THEME.secondary,
                                textTransform: 'none',
                                borderColor: THEME.border,
                            },
                        }}
                    >
                        <Tooltip title="Select and drag nodes">
                            <Button
                                onClick={() => setTool('select')}
                                variant={tool === 'select' ? 'contained' : 'outlined'}
                                sx={toolBtn('select', tool === 'select')}
                            >
                                <NearMeIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Select
                            </Button>
                        </Tooltip>
                        <Tooltip title="Click canvas to place a state">
                            <Button
                                onClick={() => setTool('addNode')}
                                variant={tool === 'addNode' ? 'contained' : 'outlined'}
                                sx={toolBtn('addNode', tool === 'addNode')}
                            >
                                <AddCircleOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                                State
                            </Button>
                        </Tooltip>
                        <Tooltip title="Drag from a green handle to another state (or back for a self-loop)">
                            <Button
                                onClick={() => setTool('edge')}
                                variant={tool === 'edge' ? 'contained' : 'outlined'}
                                sx={toolBtn('edge', tool === 'edge')}
                            >
                                <AltRouteIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Edge
                            </Button>
                        </Tooltip>
                        <Tooltip title="Delete selected">
                            <IconButton size="small" onClick={deleteSelected} sx={{ color: THEME.secondary }}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </ButtonGroup>
                </Panel>
                <Panel position="bottom-center">
                    <Typography
                        variant="caption"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            bgcolor: 'rgba(24,24,27,0.9)',
                            border: `1px solid ${THEME.border}`,
                            borderRadius: 1,
                            color: THEME.secondary,
                        }}
                    >
                        {tool === 'addNode' && 'Click empty space to add a state · double-click to rename'}
                        {tool === 'edge' &&
                            'Drag from a green dot on a state to another (or the same state) · arrows show direction'}
                        {tool === 'select' &&
                            'Double-click an edge (or its weight) to edit · Enter edits selected · Delete removes · Run animates'}
                    </Typography>
                </Panel>
            </ReactFlow>

            <Dialog open={Boolean(weightDialog)} onClose={() => setWeightDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{weightDialog?.editId ? 'Edit edge weight' : 'Edge weight'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Weight / probability"
                        value={weightInput}
                        onChange={(e) => setWeightInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && confirmWeight()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWeightDialog(null)}>Cancel</Button>
                    <Button onClick={confirmWeight} variant="contained" sx={{ bgcolor: THEME.primary, color: '#000' }}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(labelDialog)} onClose={() => setLabelDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>State name</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Label"
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && confirmLabel()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLabelDialog(null)}>Cancel</Button>
                    <Button onClick={confirmLabel} variant="contained" sx={{ bgcolor: THEME.primary, color: '#000' }}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default function MarkovGraphCanvas(props) {
    return (
        <ReactFlowProvider>
            <MarkovGraphCanvasInner {...props} />
        </ReactFlowProvider>
    );
}
